// frontend/src/components/settings/ConnectorActivityDialog.jsx
//
// The per-connector "Details" modal opened from ConnectorsPanel — a read-only
// window onto the Exact integration. Answers the question the Connectors table
// can't: "Albert added an item in Exact — did anything happen?" Shows connector
// health (with a re-authorize prompt when the token chain died), the last sync,
// current linked/imported counts, pending reviews (linking back to the existing
// Resolve wizards), the cost-skipped-on-currency signal, and the most recent
// webhook deliveries.
//
// Pure read: everything comes from GET /api/connectors/<id>/activity/. Review
// and re-authorize actions are delegated to the panel via callbacks so this
// component owns no mutations.

import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import PageBanner from '../common/PageBanner';

import StatusChip from '../common/StatusChip';
import { useConnectorActivity } from '../../hooks/useConnectorMutations';
import { translateRequestError } from '../../queries/api';
import { useFormatters } from '../../hooks/useFormatters';
import { relativeTime } from '../../utils/relativeTime';

// Webhook topic → friendly label key. Unknown topics fall back to the raw
// topic string (forward-compatible if Exact adds one before we do).
const TOPIC_KEYS = {
  Items: 'connectors.activityTopicItems',
  Accounts: 'connectors.activityTopicAccounts',
  PurchaseOrders: 'connectors.activityTopicPurchaseOrders',
  GoodsReceipts: 'connectors.activityTopicGoodsReceipts',
};

// Webhook delivery status → chip color. The keys are WebhookEvent's statuses
// and the lookup below is not defaulted; activityMaps.test.js pins the list.
const EVENT_STATUS_COLORS = {
  processed: 'success',
  failed: 'error',
  retry_pending: 'warning',
  blocked_no_scope: 'warning',
  processing: 'info',
  received: 'info',
  dropped_inactive: 'neutral',
  deferred_no_tenant: 'neutral',
  superseded: 'neutral',
};

// Health state → Alert severity. MUI Alert has no "neutral", so the inert
// states (deactivated) map to "info". The keys are HEALTH_STATES
// (exact_app/services/activity.py) and the lookup below is not defaulted;
// activityMaps.test.js pins the two lists together.
const HEALTH_SEVERITY = {
  connected: 'success',
  connecting: 'info',
  pending: 'info',
  needs_authorization: 'warning',
  deactivated: 'info',
  access_blocked: 'warning',
  unreachable: 'warning',
};

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

function HealthAlert({ health, showReauthorize, onReauthorize }) {
  const { t } = useTranslation();
  return (
    <PageBanner
      severity={HEALTH_SEVERITY[health.state]}
      messages={[t(`connectors.activityHealth_${health.state}`), health.last_error]}
      actions={[showReauthorize && { label: t('connectors.activityReauthorize'), onClick: onReauthorize }]}
    />
  );
}

function LastSyncSection({ lastSync }) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const summary = lastSync.summary;
  if (!lastSync.completed_at) {
    return (
      <Section title={t('connectors.activityLastSync')}>
        <Typography variant="body2" color="text.secondary">
          {t('connectors.activityNoSyncYet')}
        </Typography>
      </Section>
    );
  }
  return (
    <Section title={t('connectors.activityLastSync')}>
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {formatDateTime(lastSync.completed_at)}
          {summary && (
            <span style={{ marginLeft: 8 }}>
              <StatusChip
                color={summary.ok ? 'success' : 'warning'}
                label={summary.ok ? t('connectors.activitySyncOk') : t('connectors.activitySyncIssues')}
              />
            </span>
          )}
        </Typography>
        {summary && (
          <Typography variant="body2" color="text.secondary">
            {t('connectors.activitySyncSummary', {
              itemsLinked: summary.items.auto_linked,
              itemsImported: summary.items.imported,
              suppliers: summary.suppliers.created + summary.suppliers.auto_adopted,
            })}
          </Typography>
        )}
        {summary && !summary.ok && summary.errors?.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {summary.errors.map((e, i) => (
              <Typography key={i} component="li" variant="caption" color="error">
                {t(`connectors.activityErrorScope_${e.scope}`)}
                {e.ref ? ` ${e.ref}` : ''}: {e.reason}
              </Typography>
            ))}
          </Box>
        )}
      </Stack>
    </Section>
  );
}

function CountsSection({ counts }) {
  const { t } = useTranslation();
  return (
    <Section title={t('connectors.activityCounts')}>
      <Stack spacing={0.5}>
        <Typography variant="body2">
          {t('connectors.activityCountItems', {
            linked: counts.items.linked, imported: counts.items.imported,
          })}
        </Typography>
        <Typography variant="body2">
          {t('connectors.activityCountSuppliers', { linked: counts.suppliers.linked })}
        </Typography>
        <Typography variant="body2">
          {t('connectors.activityCountLocations', {
            linked: counts.locations.linked, imported: counts.locations.imported,
          })}
        </Typography>
      </Stack>
    </Section>
  );
}

function PendingReviewSection({ pending, connectorId, onReviewSuppliers, onReviewLocations, onReviewItems }) {
  const { t } = useTranslation();
  return (
    <Section title={t('connectors.activityPendingReview')}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {pending.suppliers > 0 && (
          <Button size="small" variant="outlined" color="warning"
            onClick={() => onReviewSuppliers?.(connectorId)}>
            {t('connectors.activityReviewSuppliers', { count: pending.suppliers })}
          </Button>
        )}
        {pending.locations > 0 && (
          <Button size="small" variant="outlined" color="warning"
            onClick={() => onReviewLocations?.(connectorId)}>
            {t('connectors.activityReviewLocations', { count: pending.locations })}
          </Button>
        )}
        {pending.items > 0 && (
          <Button size="small" variant="outlined" color="warning"
            onClick={() => onReviewItems?.(connectorId)}>
            {t('connectors.activityReviewItems', { count: pending.items })}
          </Button>
        )}
      </Stack>
    </Section>
  );
}

function RecentEventsSection({ events }) {
  const { t } = useTranslation();
  return (
    <Section title={t('connectors.activityRecent')}>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('connectors.activityRecentEmpty')}
        </Typography>
      ) : (
        <Stack spacing={1} divider={<Divider flexItem />}>
          {events.map((e) => (
            <Box key={e.id}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2">
                  {TOPIC_KEYS[e.topic] ? t(TOPIC_KEYS[e.topic]) : e.topic}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {relativeTime(e.received_at, t)}
                  </Typography>
                  <StatusChip color={EVENT_STATUS_COLORS[e.status]}
                    label={t(`connectors.activityEventStatus_${e.status}`)} />
                </Stack>
              </Stack>
              {e.error_message && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                  {e.error_message}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  );
}

/** The loaded activity: every key below is always sent (exact_app/services/activity.py
 *  build_connector_activity). */
function ActivityBody({ data, connectorId, onReviewSuppliers, onReviewLocations, onReviewItems, onAuthorize }) {
  const { t } = useTranslation();
  const pending = data.pending_review;
  const costSkipped = data.skipped.cost_currency_mismatch;
  const hasPending = pending.suppliers > 0 || pending.items > 0 || pending.locations > 0;
  // The re-authorize CTA: only when the token chain is dead AND the connector
  // was previously connected (status ACTIVE) — a never-connected one is a
  // plain "Authorize", which the panel already surfaces in the row.
  const showReauthorize = data.health.state === 'needs_authorization' && data.connector.status === 'ACTIVE';

  return (
    <>
      <HealthAlert
        health={data.health}
        showReauthorize={showReauthorize}
        onReauthorize={() => onAuthorize?.(connectorId)}
      />

      <LastSyncSection lastSync={data.last_sync} />

      {/* Skipped-with-reason (lives here, NOT Data Health) */}
      <PageBanner
        severity="warning"
        messages={[costSkipped > 0 && t('connectors.activityCostSkipped', { count: costSkipped })]}
      />

      <CountsSection counts={data.counts} />

      {/* Pending reviews → reopen the existing Resolve wizards */}
      {hasPending && (
        <PendingReviewSection
          pending={pending}
          connectorId={connectorId}
          onReviewSuppliers={onReviewSuppliers}
          onReviewLocations={onReviewLocations}
          onReviewItems={onReviewItems}
        />
      )}

      <RecentEventsSection events={data.recent_events} />
    </>
  );
}

export default function ConnectorActivityDialog({
  connectorId, open, onClose, ...actions
}) {
  const { t } = useTranslation();
  const { data, isPending, error } = useConnectorActivity(connectorId, open);

  return (
    <Dialog open={open} onClose={onClose} className="dialog-form">
      <DialogTitle>{t('connectors.activityTitle')}</DialogTitle>
      <DialogContent>
        {isPending ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : error ? (
          <Alert severity="error">{translateRequestError(error, t, 'connectors.activityLoadFailed')}</Alert>
        ) : (
          <ActivityBody data={data} connectorId={connectorId} {...actions} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('btn.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
